import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import {
  Nis2EarlyWarningData, Nis2EntityProfile, Nis2FinalData, Nis2IntermediateData,
  Nis2NotificationData, Nis2Significance, Nis2Stage, Nis2StageDataMap,
} from '../../types/nis2';
import {
  STAGE_META, SEVERITY_OPTIONS, THREAT_CATEGORY_OPTIONS, finalReportTitle, formatDateTime,
  indicatedCriteria, labelFor,
} from './nis2Helpers';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica', fontSize: 10, paddingTop: 44, paddingBottom: 56, paddingHorizontal: 52,
    backgroundColor: '#ffffff', color: '#1a1a1a',
  },
  header: {
    marginBottom: 20, paddingBottom: 12, borderBottomWidth: 2, borderBottomColor: '#1a1a1a',
    borderBottomStyle: 'solid', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start',
  },
  brandName: { fontSize: 18, fontFamily: 'Helvetica-Bold', letterSpacing: 1 },
  brandSub: { fontSize: 8, color: '#888888', marginTop: 2 },
  docMeta: { fontSize: 8, color: '#666666', textAlign: 'right' },
  titleBlock: { marginBottom: 16 },
  docTitle: { fontSize: 15, fontFamily: 'Helvetica-Bold', letterSpacing: 0.3, marginBottom: 4 },
  docSubtitle: { fontSize: 8, color: '#666666' },
  banner: {
    backgroundColor: '#fff3cd', borderLeftWidth: 3, borderLeftColor: '#e6a817', borderLeftStyle: 'solid',
    paddingVertical: 6, paddingHorizontal: 10, marginBottom: 16,
  },
  bannerText: { fontSize: 8, color: '#7a5c00', fontFamily: 'Helvetica-Bold' },
  section: { marginBottom: 14 },
  sectionTitle: {
    fontSize: 8, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase', letterSpacing: 0.8,
    color: '#ffffff', backgroundColor: '#333333', paddingVertical: 4, paddingHorizontal: 8, marginBottom: 8,
  },
  fieldRow: { flexDirection: 'row', marginBottom: 5, alignItems: 'flex-start' },
  fieldLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#555555', width: 170, flexShrink: 0 },
  fieldValue: { fontSize: 9, color: '#1a1a1a', flex: 1 },
  boxText: { fontSize: 9, color: '#1a1a1a', lineHeight: 1.4 },
  checkboxRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 5 },
  checkboxBox: {
    width: 10, height: 10, borderWidth: 1, borderColor: '#555555', borderStyle: 'solid', marginRight: 7,
    flexShrink: 0, alignItems: 'center', justifyContent: 'center',
  },
  checkboxFill: { width: 6, height: 6, backgroundColor: '#1a1a1a' },
  checkboxLabel: { fontSize: 9 },
  box: {
    backgroundColor: '#f8f8f8', borderWidth: 1, borderColor: '#dddddd', borderStyle: 'solid',
    paddingVertical: 6, paddingHorizontal: 8, marginTop: 2, marginBottom: 6,
  },
  table: { borderWidth: 1, borderColor: '#dddddd', borderStyle: 'solid', marginTop: 2 },
  tableRow: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#eeeeee', borderBottomStyle: 'solid' },
  tableHead: { backgroundColor: '#f0f0f0' },
  cellType: { width: 90, paddingVertical: 3, paddingHorizontal: 6, fontSize: 8 },
  cellValue: { flex: 1, paddingVertical: 3, paddingHorizontal: 6, fontSize: 8, fontFamily: 'Courier' },
  legalNote: {
    fontSize: 7, color: '#888888', fontStyle: 'italic', marginTop: 10, paddingVertical: 6, paddingHorizontal: 8,
    backgroundColor: '#f5f5f5', borderLeftWidth: 2, borderLeftColor: '#cccccc', borderLeftStyle: 'solid',
  },
  signatureArea: { marginTop: 24, flexDirection: 'row', justifyContent: 'space-between' },
  signatureBlock: { width: '42%' },
  signatureLine: { borderTopWidth: 1, borderTopColor: '#aaaaaa', borderTopStyle: 'solid', marginBottom: 4 },
  signatureLabel: { fontSize: 8, color: '#666666' },
  footer: {
    position: 'absolute', bottom: 28, left: 52, right: 52, borderTopWidth: 1, borderTopColor: '#cccccc',
    borderTopStyle: 'solid', paddingTop: 6, flexDirection: 'row', justifyContent: 'space-between',
  },
  footerText: { fontSize: 7, color: '#aaaaaa' },
});

// ─── Primitives ──────────────────────────────────────────────────────────────

const Field: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <View style={styles.fieldRow}>
    <Text style={styles.fieldLabel}>{label}:</Text>
    <Text style={styles.fieldValue}>{value === null || value === undefined || value === '' ? '—' : String(value)}</Text>
  </View>
);

const Box: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <>
    <View style={styles.fieldRow}><Text style={styles.fieldLabel}>{label}:</Text></View>
    <View style={styles.box}><Text style={styles.boxText}>{value || '—'}</Text></View>
  </>
);

const Check: React.FC<{ checked?: boolean; label: string }> = ({ checked, label }) => (
  <View style={styles.checkboxRow}>
    <View style={styles.checkboxBox}>{checked ? <View style={styles.checkboxFill} /> : null}</View>
    <Text style={styles.checkboxLabel}>{label}</Text>
  </View>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <View style={styles.section} wrap={false}>
    <Text style={styles.sectionTitle}>{title}</Text>
    {children}
  </View>
);

const eur = (n?: number | null) =>
  n === null || n === undefined ? null : `EUR ${n.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`;

const states = (list?: string[]) => (list && list.length ? list.join(', ') : null);

// ─── Stage bodies ────────────────────────────────────────────────────────────

const EarlyWarningBody: React.FC<{ d: Nis2EarlyWarningData }> = ({ d }) => (
  <>
    <Section title="3. Incident information">
      <Field label="Detection date/time" value={formatDateTime(d.detection_datetime)} />
      <Field label="Incident type" value={d.incident_type} />
      <Field label="Affected services / systems" value={d.affected_services} />
      <Box label="Initial summary" value={d.initial_summary} />
    </Section>
    <Section title="4. Early warning indicators">
      <Check checked={d.is_malicious_or_unlawful} label="Suspected to be caused by unlawful or malicious acts" />
      <Check checked={d.suspected_cross_border_impact} label="Could have a cross-border impact" />
      <Field label="Member States potentially affected" value={states(d.affected_member_states)} />
    </Section>
    <Section title="5. Response">
      <Check checked={d.containment_initiated} label="Containment initiated" />
      <Box label="Measures taken" value={d.measures_taken} />
      <Check checked={d.assistance_requested} label="Assistance / operational guidance requested from the CSIRT" />
      {d.assistance_requested ? <Box label="Assistance details" value={d.assistance_details} /> : null}
    </Section>
  </>
);

const NotificationBody: React.FC<{ d: Nis2NotificationData }> = ({ d }) => (
  <>
    <Section title="3. Initial assessment">
      <Field label="Severity" value={labelFor(SEVERITY_OPTIONS, d.severity)} />
      <Field label="Threat category" value={labelFor(THREAT_CATEGORY_OPTIONS, d.threat_category)} />
      <Box label="Assessment of severity and impact" value={d.initial_assessment} />
    </Section>
    <Section title="4. Impact estimates">
      <Field label="Affected users" value={d.affected_users_count?.toLocaleString('en-GB')} />
      <Field label="Service downtime (minutes)" value={d.downtime_minutes} />
      <Field label="Estimated financial loss" value={eur(d.estimated_financial_loss_eur)} />
      <Check checked={d.gdpr_breach_triggered} label="Personal data breach — GDPR Art. 33 notification also triggered" />
    </Section>
    <Section title="5. Update of early warning information">
      <Check checked={d.is_malicious_or_unlawful} label="Suspected to be caused by unlawful or malicious acts" />
      <Check checked={d.suspected_cross_border_impact} label="Could have a cross-border impact" />
      <Field label="Member States potentially affected" value={states(d.affected_member_states)} />
      <Box label="Measures taken" value={d.measures_taken} />
    </Section>
    {/* Keep short IoC lists together; long ones must be allowed to break across pages */}
    <View style={styles.section} wrap={(d.indicators_of_compromise?.length || 0) > 15}>
      <Text style={styles.sectionTitle}>6. Indicators of compromise</Text>
      {d.indicators_of_compromise?.length ? (
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHead]}>
            <Text style={styles.cellType}>Type</Text>
            <Text style={[styles.cellValue, { fontFamily: 'Helvetica-Bold' }]}>Value</Text>
          </View>
          {d.indicators_of_compromise.map((ioc, i) => (
            <View key={i} style={styles.tableRow} wrap={false}>
              <Text style={styles.cellType}>{ioc.type}</Text>
              <Text style={styles.cellValue}>{ioc.value}</Text>
            </View>
          ))}
        </View>
      ) : <Text style={styles.boxText}>None available at this stage.</Text>}
    </View>
  </>
);

const IntermediateBody: React.FC<{ d: Nis2IntermediateData }> = ({ d }) => (
  <Section title="3. Status update">
    <Field label="Requested by authority on" value={formatDateTime(d.requested_at)} />
    <Field label="Request reference" value={d.request_reference} />
    <Field label="Current status" value={d.current_status ? d.current_status[0].toUpperCase() + d.current_status.slice(1) : ''} />
    <Box label="Relevant status updates" value={d.status_update} />
  </Section>
);

const FinalBody: React.FC<{ d: Nis2FinalData }> = ({ d }) => (
  <>
    <Section title="3. Incident description">
      <Check checked={d.is_incident_resolved} label="Incident handling concluded" />
      <Field label="Handling concluded at" value={formatDateTime(d.handling_concluded_at)} />
      <Field label="Severity" value={labelFor(SEVERITY_OPTIONS, d.severity)} />
      <Box label="Detailed description" value={d.detailed_description} />
      <Box label="Impact" value={d.impact_summary} />
    </Section>
    <Section title="4. Threat and root cause">
      <Box label="Type of threat / root cause" value={d.root_cause_analysis} />
      <Field label="MITRE ATT&CK techniques" value={d.mitre_attack_techniques?.join(', ')} />
    </Section>
    <Section title="5. Mitigation">
      <Box label="Applied mitigation measures" value={d.mitigations_implemented} />
      <Box label="Ongoing mitigation measures" value={d.mitigations_ongoing} />
    </Section>
    <Section title="6. Cross-border impact and lessons learned">
      <Box label="Cross-border impact" value={d.cross_border_impact} />
      <Box label="Lessons learned" value={d.lessons_learned} />
    </Section>
  </>
);

// ─── Document ────────────────────────────────────────────────────────────────

export interface Nis2PdfProps<S extends Nis2Stage = Nis2Stage> {
  stage: S;
  data: Nis2StageDataMap[S];
  entity: Nis2EntityProfile;
  significance: Nis2Significance;
  ticketId: string;
  awarenessAt: string | null;
  dueAt: string | null;
  generatedAt: string;
}

const Nis2ReportPdf: React.FC<Nis2PdfProps> = ({
  stage, data, entity, significance, ticketId, awarenessAt, dueAt, generatedAt,
}) => {
  const meta = STAGE_META[stage];
  const title = stage === 'final' ? finalReportTitle(data as Nis2FinalData) : meta.title;
  const criteria = indicatedCriteria(significance);

  return (
    <Document title={`NIS2 ${title} — ${ticketId}`} author="SOCMate"
      subject={`NIS2 ${title} — Directive (EU) 2022/2555, ${meta.article}`}>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View>
            <Text style={styles.brandName}>SOCMate</Text>
            <Text style={styles.brandSub}>Managed Security Operations Platform</Text>
          </View>
          <View>
            <Text style={styles.docMeta}>Incident ref: {ticketId}</Text>
            <Text style={styles.docMeta}>Generated: {generatedAt}</Text>
          </View>
        </View>

        <View style={styles.titleBlock}>
          <Text style={styles.docTitle}>NIS2 INCIDENT REPORT — {title.toUpperCase()}</Text>
          <Text style={styles.docSubtitle}>
            Directive (EU) 2022/2555, {meta.article} · Commission Implementing Regulation (EU) 2024/2690
          </Text>
        </View>

        {dueAt ? (
          <View style={styles.banner}>
            <Text style={styles.bannerText}>
              Statutory deadline: {formatDateTime(dueAt)} · Recipient: {entity.authority || 'national CSIRT / competent authority'}
            </Text>
          </View>
        ) : null}

        <Section title="1. Notifying entity">
          <Field label="Entity name" value={entity.entity_name} />
          <Field label="National registration ID" value={entity.registration_id} />
          <Field label="Entity classification" value={entity.entity_type ? `${entity.entity_type[0].toUpperCase()}${entity.entity_type.slice(1)} entity` : ''} />
          <Field label="Sector" value={entity.sector} />
          <Field label="Member State of establishment" value={entity.member_state} />
          <Field label="CSIRT / competent authority" value={entity.authority} />
          <Field label="Point of contact" value={[entity.contact_name, entity.contact_email, entity.contact_phone].filter(Boolean).join(' · ')} />
        </Section>

        <Section title="2. Incident reference">
          <Field label="Awareness of significant incident" value={formatDateTime(awarenessAt)} />
          <Field label="Significance assessment" value={
            significance.decision === 'significant' ? 'Assessed as significant (Art. 23(3))'
              : significance.decision === 'not_significant' ? 'Assessed as not significant (voluntary report, Art. 30)'
                : 'Assessment ongoing'} />
          {criteria.length ? <Field label="Criteria considered" value={criteria.join('; ')} /> : null}
          {significance.decision_rationale ? <Box label="Rationale" value={significance.decision_rationale} /> : null}
        </Section>

        {stage === 'early_warning' && <EarlyWarningBody d={data as Nis2EarlyWarningData} />}
        {stage === 'notification' && <NotificationBody d={data as Nis2NotificationData} />}
        {stage === 'intermediate' && <IntermediateBody d={data as Nis2IntermediateData} />}
        {stage === 'final' && <FinalBody d={data as Nis2FinalData} />}

        <Text style={styles.legalNote}>
          Art. 23(1) of Directive (EU) 2022/2555: &quot;The mere act of notification shall not subject the
          notifying entity to increased liability.&quot;
        </Text>

        <View style={styles.signatureArea} wrap={false}>
          <View style={styles.signatureBlock}>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Signature of the person responsible for security</Text>
          </View>
          <View style={styles.signatureBlock}>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Date and place</Text>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>SOCMate — Generated {generatedAt}</Text>
          <Text style={styles.footerText} render={({ pageNumber, totalPages }) =>
            `NIS2 ${title} · ${meta.article} · ${pageNumber}/${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default Nis2ReportPdf;
